import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";
import { Plug, RefreshCw, Check, AlertCircle, Power } from "lucide-react";

const InputRow = ({ label, value, onChange, type = "text", placeholder, testid }) => (
  <div>
    <label className="overline mb-1 block text-[#736B66]">{label}</label>
    <input
      className="luxury-input-box w-full"
      type={type}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
    />
  </div>
);

const Card = ({ title, badge, children }) => (
  <div className="bg-white border border-[#EBE5DF] p-7">
    <div className="flex items-start justify-between mb-5">
      <h3 className="font-serif text-2xl flex items-center gap-3">
        <Plug size={20} className="text-[#D4AF37]" />
        {title}
      </h3>
      {badge}
    </div>
    {children}
  </div>
);

const StatusBadge = ({ enabled }) => (
  <span
    className={`text-[10px] tracking-widest uppercase font-semibold px-3 py-1 rounded ${
      enabled ? "bg-[#D4AF37]/15 text-[#7A6017]" : "bg-[#736B66]/10 text-[#736B66]"
    }`}
  >
    {enabled ? "ON" : "OFF"}
  </span>
);

const AdminIntegrations = () => {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Per-provider draft state
  const [shopify, setShopify] = useState({ store_domain: "", access_token: "", webhook_secret: "" });
  const [bigbuy, setBigbuy] = useState({ environment: "production", api_key: "", webhook_token: "" });
  const [cron, setCron] = useState({ enabled: false, interval_hours: 6 });
  const [taxonomies, setTaxonomies] = useState(null);
  const [taxonomyMap, setTaxonomyMap] = useState({});
  const [taxLoading, setTaxLoading] = useState(false);

  const load = () =>
    api.get("/admin/integrations").then((r) => {
      setData(r.data);
      setShopify((s) => ({ ...s, store_domain: r.data.shopify.store_domain || "" }));
      setBigbuy((s) => ({ ...s, environment: r.data.bigbuy.environment || "production" }));
      setCron({
        enabled: !!r.data.cron?.enabled,
        interval_hours: r.data.cron?.interval_hours || 6,
      });
    });

  useEffect(() => {
    load();
  }, []);

  const updateShopify = async (extra = {}) => {
    setLoading(true);
    try {
      const payload = { ...extra };
      if (shopify.store_domain) payload.store_domain = shopify.store_domain;
      if (shopify.access_token) payload.access_token = shopify.access_token;
      if (shopify.webhook_secret) payload.webhook_secret = shopify.webhook_secret;
      await api.put("/admin/integrations/shopify", payload);
      setShopify({ ...shopify, access_token: "", webhook_secret: "" });
      await load();
      toast.success("Shopify settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  const updateBigbuy = async (extra = {}) => {
    setLoading(true);
    try {
      const payload = { ...extra };
      if (bigbuy.environment) payload.environment = bigbuy.environment;
      if (bigbuy.api_key) payload.api_key = bigbuy.api_key;
      if (bigbuy.webhook_token) payload.webhook_token = bigbuy.webhook_token;
      await api.put("/admin/integrations/bigbuy", payload);
      setBigbuy({ ...bigbuy, api_key: "", webhook_token: "" });
      await load();
      toast.success("BigBuy settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  const fetchTaxonomies = async () => {
    setTaxLoading(true);
    try {
      const res = await api.get("/admin/integrations/bigbuy/taxonomies?iso_code=el");
      setTaxonomies(res.data.taxonomies || []);
      setTaxonomyMap(res.data.mapping || {});
      toast.success(`Loaded ${(res.data.taxonomies || []).length} taxonomies`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to fetch taxonomies");
    } finally {
      setTaxLoading(false);
    }
  };

  const saveTaxonomyMap = async () => {
    setTaxLoading(true);
    try {
      const res = await api.put("/admin/integrations/bigbuy/taxonomies", { mapping: taxonomyMap });
      toast.success(`Saved ${res.data.count} mappings`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setTaxLoading(false);
    }
  };

  const updateCron = async (extra = {}) => {
    setLoading(true);
    try {
      const payload = { ...cron, ...extra };
      const res = await api.put("/admin/integrations/cron", payload);
      // Sync from server (server clamps interval 1..72)
      setCron({
        enabled: !!res.data?.enabled,
        interval_hours: parseInt(res.data?.interval_hours, 10) || 6,
      });
      toast.success("Cron settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async (provider) => {
    setLoading(true);
    try {
      const res = await api.post(`/admin/integrations/${provider}/test`);
      toast.success(
        provider === "shopify"
          ? `✓ ${res.data?.shop?.name || "Connected"} (${res.data?.shop?.domain || ""})`
          : `✓ Connected (${res.data?.environment})`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Connection failed");
    } finally {
      setLoading(false);
    }
  };

  const sync = async (provider) => {
    setLoading(true);
    try {
      const res = await api.post(`/admin/integrations/${provider}/sync`);
      if (res.data?.ok) {
        toast.success(
          `✓ Imported ${res.data.imported || 0}, updated ${res.data.updated || 0}`
        );
        await load();
      } else {
        toast.error(res.data?.reason || "Sync failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync error");
    } finally {
      setLoading(false);
    }
  };

  const registerShopifyWebhooks = async () => {
    setLoading(true);
    try {
      const res = await api.post("/admin/integrations/shopify/webhooks/register");
      const c = res.data?.created?.length || 0;
      const s = res.data?.skipped?.length || 0;
      const e = res.data?.errors?.length || 0;
      toast.success(`Webhooks: ${c} created, ${s} already exist, ${e} errors`);
      if (e > 0) {
        console.warn("Webhook errors:", res.data.errors);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Webhook registration failed");
    } finally {
      setLoading(false);
    }
  };

  if (!data) {
    return <div className="text-[#736B66]">Loading...</div>;
  }

  return (
    <div className="space-y-6 max-w-4xl" data-testid="integrations-panel">
      {/* SHOPIFY */}
      <Card title="Shopify" badge={<StatusBadge enabled={data.shopify.enabled} />}>
        <p className="text-sm text-[#736B66] mb-5">
          Συνδέστε το Shopify store σας ώστε να εισάγουμε προϊόντα και να προωθούμε τις παραγγελίες
          σας στο Shopify για fulfillment.
        </p>
        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <InputRow
            label="Store domain"
            value={shopify.store_domain}
            onChange={(v) => setShopify({ ...shopify, store_domain: v })}
            placeholder="mystore.myshopify.com"
            testid="shopify-domain"
          />
          <InputRow
            label="Admin API access token"
            value={shopify.access_token}
            onChange={(v) => setShopify({ ...shopify, access_token: v })}
            placeholder={data.shopify.has_token ? data.shopify.access_token_mask : "shpat_..."}
            type="password"
            testid="shopify-token"
          />
        </div>
        <p className="text-xs text-[#736B66] mb-5">
          Το token παράγεται από: Shopify Admin → Settings → Apps and sales channels → Develop apps →
          Create app → Admin API access tokens (scopes:{" "}
          <code>read_products, read_inventory, write_orders, write_customers</code>).
        </p>
        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <InputRow
            label="Webhook secret (optional)"
            value={shopify.webhook_secret}
            onChange={(v) => setShopify({ ...shopify, webhook_secret: v })}
            placeholder={
              data.shopify.has_webhook_secret
                ? data.shopify.webhook_secret_mask
                : "Shopify webhook signing secret"
            }
            type="password"
            testid="shopify-webhook-secret"
          />
          <div className="text-xs text-[#736B66] pt-7">
            Webhook URL:{" "}
            <code className="text-[10px] break-all">
              {(process.env.REACT_APP_BACKEND_URL || "") + "/api/webhook/shopify/{topic}"}
            </code>
          </div>
        </div>
        {data.shopify.last_sync_at && (
          <p className="text-xs text-[#736B66] mb-4">
            Last sync: {new Date(data.shopify.last_sync_at).toLocaleString()} · imported{" "}
            {data.shopify.last_sync_imported} · updated {data.shopify.last_sync_updated}
          </p>
        )}
        <div className="flex flex-wrap gap-3">
          <button
            className="luxury-btn"
            onClick={() => updateShopify()}
            disabled={loading}
            data-testid="shopify-save"
          >
            Αποθήκευση
          </button>
          <button
            className="luxury-btn-outline"
            onClick={() => updateShopify({ enabled: !data.shopify.enabled })}
            disabled={loading || !data.shopify.has_token}
            data-testid="shopify-toggle"
          >
            <Power size={14} /> {data.shopify.enabled ? "Disable" : "Enable"}
          </button>
          <button
            className="luxury-btn-outline"
            onClick={() => testConnection("shopify")}
            disabled={loading || !data.shopify.has_token}
            data-testid="shopify-test"
          >
            <Check size={14} /> Test connection
          </button>
          <button
            className="luxury-btn-outline"
            onClick={() => sync("shopify")}
            disabled={loading || !data.shopify.enabled}
            data-testid="shopify-sync"
          >
            <RefreshCw size={14} /> Sync products now
          </button>
          <button
            className="luxury-btn-outline"
            onClick={registerShopifyWebhooks}
            disabled={loading || !data.shopify.has_token}
            data-testid="shopify-register-webhooks"
          >
            <Plug size={14} /> Auto-register webhooks
          </button>
        </div>
      </Card>

      {/* BIGBUY */}
      <Card title="BigBuy (Dropshipping)" badge={<StatusBadge enabled={data.bigbuy.enabled} />}>
        <p className="text-sm text-[#736B66] mb-5">
          Συνδέστε τον BigBuy λογαριασμό σας για εισαγωγή catalog και αυτόματη αποστολή παραγγελιών
          για dropshipping.
        </p>
        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="overline mb-1 block text-[#736B66]">Environment</label>
            <select
              className="luxury-input-box w-full"
              value={bigbuy.environment}
              onChange={(e) => setBigbuy({ ...bigbuy, environment: e.target.value })}
              data-testid="bigbuy-env"
            >
              <option value="production">Production</option>
              <option value="sandbox">Sandbox (testing)</option>
            </select>
          </div>
          <InputRow
            label="API key"
            value={bigbuy.api_key}
            onChange={(v) => setBigbuy({ ...bigbuy, api_key: v })}
            placeholder={data.bigbuy.has_key ? data.bigbuy.api_key_mask : "Your BigBuy API key"}
            type="password"
            testid="bigbuy-key"
          />
        </div>
        <p className="text-xs text-[#736B66] mb-5">
          Από BigBuy Control Panel → My account → Synchronize with BigBuy → Show API key. Επιλέξτε το
          production key για το πραγματικό σας account.
        </p>
        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <InputRow
            label="Webhook token (optional)"
            value={bigbuy.webhook_token}
            onChange={(v) => setBigbuy({ ...bigbuy, webhook_token: v })}
            placeholder={
              data.bigbuy.has_webhook_token
                ? data.bigbuy.webhook_token_mask
                : "Bearer token for webhook auth"
            }
            type="password"
            testid="bigbuy-webhook-token"
          />
          <div className="text-xs text-[#736B66] pt-7">
            Webhook URL:{" "}
            <code className="text-[10px] break-all">
              {(process.env.REACT_APP_BACKEND_URL || "") + "/api/webhook/bigbuy/{event}"}
            </code>
          </div>
        </div>
        {data.bigbuy.last_sync_at && (
          <p className="text-xs text-[#736B66] mb-4">
            Last sync: {new Date(data.bigbuy.last_sync_at).toLocaleString()} · imported{" "}
            {data.bigbuy.last_sync_imported} · updated {data.bigbuy.last_sync_updated}
          </p>
        )}
        <div className="flex flex-wrap gap-3">
          <button
            className="luxury-btn"
            onClick={() => updateBigbuy()}
            disabled={loading}
            data-testid="bigbuy-save"
          >
            Αποθήκευση
          </button>
          <button
            className="luxury-btn-outline"
            onClick={() => updateBigbuy({ enabled: !data.bigbuy.enabled })}
            disabled={loading || !data.bigbuy.has_key}
            data-testid="bigbuy-toggle"
          >
            <Power size={14} /> {data.bigbuy.enabled ? "Disable" : "Enable"}
          </button>
          <button
            className="luxury-btn-outline"
            onClick={() => testConnection("bigbuy")}
            disabled={loading || !data.bigbuy.has_key}
            data-testid="bigbuy-test"
          >
            <Check size={14} /> Test connection
          </button>
          <button
            className="luxury-btn-outline"
            onClick={() => sync("bigbuy")}
            disabled={loading || !data.bigbuy.enabled}
            data-testid="bigbuy-sync"
          >
            <RefreshCw size={14} /> Sync catalog now
          </button>
        </div>

        {/* TAXONOMY MAPPING */}
        <div className="mt-7 pt-6 border-t border-[#EBE5DF]" data-testid="taxonomy-section">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-serif text-lg">BigBuy → Amazypet category mapping</h4>
            <span className="text-xs text-[#736B66]">
              {data.bigbuy.taxonomy_map_count} ενεργές αντιστοιχίσεις
            </span>
          </div>
          <p className="text-xs text-[#736B66] mb-4">
            Ορίστε σε ποια Amazypet κατηγορία πρέπει να μπει κάθε BigBuy taxonomy κατά τον συγχρονισμό.
            Όσες αφεθούν κενές, τα προϊόντα τους θα μπουν στο default (&quot;skylos&quot;).
          </p>
          <div className="flex flex-wrap gap-3 mb-4">
            <button
              className="luxury-btn-outline"
              onClick={fetchTaxonomies}
              disabled={taxLoading || !data.bigbuy.has_key}
              data-testid="taxonomy-fetch"
            >
              {taxLoading ? "Loading..." : "Fetch taxonomies"}
            </button>
            {taxonomies && (
              <button
                className="luxury-btn"
                onClick={saveTaxonomyMap}
                disabled={taxLoading}
                data-testid="taxonomy-save"
              >
                Save mapping
              </button>
            )}
          </div>
          {taxonomies && taxonomies.length > 0 && (
            <div className="max-h-80 overflow-y-auto border border-[#EBE5DF]">
              <table className="w-full text-sm">
                <thead className="bg-[#F5F2ED] sticky top-0">
                  <tr>
                    <th className="text-left p-2 text-xs uppercase tracking-wider">BigBuy taxonomy</th>
                    <th className="text-left p-2 text-xs uppercase tracking-wider">Amazypet slug</th>
                  </tr>
                </thead>
                <tbody>
                  {taxonomies.map((tax) => {
                    const tid = String(tax.id);
                    return (
                      <tr key={tid} className="border-t border-[#EBE5DF]">
                        <td className="p-2">
                          <div className="font-medium">{tax.name || `#${tid}`}</div>
                          <div className="text-[10px] text-[#736B66]">id: {tid}</div>
                        </td>
                        <td className="p-2">
                          <input
                            className="luxury-input-box w-full text-xs"
                            placeholder="skylos / gata / ptina / psaria / erpeta / troktika"
                            value={taxonomyMap[tid] || ""}
                            onChange={(e) =>
                              setTaxonomyMap({ ...taxonomyMap, [tid]: e.target.value })
                            }
                            data-testid={`taxonomy-input-${tid}`}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      {/* CRON */}
      <Card title="Scheduled Sync (cron)" badge={<StatusBadge enabled={cron.enabled} />}>
        <p className="text-sm text-[#736B66] mb-5">
          Αυτόματος συγχρονισμός catalog Shopify + BigBuy ανά χρονικό διάστημα.
        </p>
        <div className="grid sm:grid-cols-2 gap-4 mb-5">
          <div className="flex items-end gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={cron.enabled}
                onChange={(e) => setCron({ ...cron, enabled: e.target.checked })}
                data-testid="cron-enabled"
              />
              <span className="text-sm">Ενεργοποίηση</span>
            </label>
          </div>
          <div>
            <label className="overline mb-1 block text-[#736B66]">Interval (hours)</label>
            <input
              type="number"
              min={1}
              max={72}
              className="luxury-input-box w-full"
              value={cron.interval_hours}
              onChange={(e) =>
                setCron({ ...cron, interval_hours: parseInt(e.target.value, 10) || 6 })
              }
              data-testid="cron-interval"
            />
          </div>
        </div>
        <button
          className="luxury-btn"
          onClick={() => updateCron()}
          disabled={loading}
          data-testid="cron-save"
        >
          Αποθήκευση
        </button>
        <p className="text-xs text-[#736B66] mt-4 flex items-center gap-2">
          <AlertCircle size={14} />
          Οι αλλαγές στο cron εφαρμόζονται την επόμενη φορά που τρέξει το background task.
        </p>
      </Card>
    </div>
  );
};

export default AdminIntegrations;
